<template>
    <Dialog :dialog="dlg" xno-sub-title no-content-padding>

        <Datatable :dt="dt" class="full-height">
            <template v-slot:menu ="{ item }">
                <ActionMenuItem action="salesinvoice_payment.open" @click="()=>onOpenPayment(item)"></ActionMenuItem>
                <v-divider></v-divider>
                <ActionMenuItem action="salesinvoice_payment.remove" @click="()=>removePayment(item)"></ActionMenuItem>
            </template>

        </Datatable>

        <template v-slot:toolbar-left>
            
            <ToolbarField label="Factuurbedrag">
                <AmountText class="text-primary" dusk="inv_total" v-model='model.inv_total' :currency='model.currency'/>
            </ToolbarField>
            <ToolbarField class="ml-8" label="Openstaand">
                <AmountText class="text-primary" dusk="inv_open" v-model='model.inv_open' :currency='model.currency'/>
            </ToolbarField>

        </template>

        <template v-slot:footer-left>
            <ActionButton sub action='salesinvoice_payment.create' @click="()=>onAddPayment()"></ActionButton>
        </template>

        <DialogSalesInvoicePayment/>

    </Dialog>
</template>



<script setup>
    import useDataTable from '@app/useDataTable'
    import Datatable from '@datatable/Datatable'
    import ActionMenuItem from "@controls/menu/ActionMenuItem.vue";
    import { salesinvoice_payments as api } from '@app/api'
    import string from '@lib/string'
    import bool from '@lib/bool'
    import ActionButton from '@controls/buttons/ActionButton'
    import action from '@app/action'
    import { ref, onMounted, onUnmounted } from 'vue'
    import clsDialog from '@cls/clsDialog'
    import Dialog from '@shared/ui/dialogs/ActionDialog'
    import DialogSalesInvoicePayment from '@/ui/dialogs/salesinvoice/DialogSalesInvoicePayment'
    import FormRow from '@controls/forms/FormRow'
    import AmountText from '@controls/text/Amount.vue'
    import ToolbarField from '@shared/ui/dialogs/ToolbarField'
    import eventbus from '@app/eventbus'
    import noty from '@shared/lib/noty'

    import appDlg from '@app/dlg'
/**
 * Declare the data table first as 
 */
    var fmtType = function(a,b,item) {
        if (item.accountancy) {
            return "Accountant";
        }
        if (item.mollie) {
            return "Mollie";
        }
        return "Betaling";
    }

    var headers = [
        { type: 'cmd' },
        { text: "Type",                     value: "type", fmt:fmtType, type:"open", open:(item, header)=>onOpenPayment(item)},
        { text: "Bedrag",                   value: "amount", type: 'amount'     },
        { isUtc: true, text: "Gewijzigd",                value: "updated_at",    type: 'datetime',   visible: false},
		{ text: "Gewijzigd door",           value: "updated_by",                        visible: false},
		{ isUtc: true, text: "Aangemaakt",               value: "created_at",    type: 'datetime',   visible: true},
		{ text: "Aangemaakt door",          value: "created_by",                        visible: true},
        { visible: false, text: "Omschrijving",             value: "description",},

    ]

    var dt = useDataTable("dt_salesinvoice_payments", "salesinvoice_payment", api, headers, {
        itemName: {
            prefix: 'de',
            single: 'betaling',
            plural: 'betalingen',
        }, parent: {
            id_invoice: null // model.id
        },
        noAutoSearch: true, // Search not until the parent id is set.
        canArchive: false
    })


    class clsPModel {
        id = null;
        inv_total   = null;
        inv_open    = null;
        inv_status    = null;
        inv_number  = null;
        isLoading = false;
        isDataLoading = false;

        get subTitle() {
            if (this.isLoading || this.isDataLoading) {
                return 'Laden...';
            }
            return `Factuur ${this.inv_number||""}`;
        }
        async load(id, params) {
            this.isLoading = true;
            try {
                var result = await api.loadInfo(id)
                this.inv_total = result.data.inv_total;
                this.inv_open  = result.data.inv_open;
                this.inv_status  = result.data.inv_status;
                this.inv_number  = result.data.inv_number;
                
                this.id = id;
                dt.setParent({id_invoice: id}, true); // true: search
            } finally {
                this.isLoading = false;
            }
        }
    }

    let model = ref (new clsPModel());

    class clsPaymentsDialog extends clsDialog {

        // Instead of close with a bang, close with the invoice status so that the status is updated automatically when 
        // payments are added. Note that this changes the dialog / model status. The datatable is updated via other ways.
        async closeDialog(force) {
            if (this.fnResolve) {
                this.fnResolve(this.model);
            }
            this.open = false;
        }
    }

    let dlg = ref(
        new clsPaymentsDialog("salesinvoice_payments", "Betaalgeschiedenis", model, [], {            
            setKeyOnOpen: false,
            icon: 'payment',
            maxWidth:'1200px', 
            showNote: false,
            showAttachment: false,
            save:false,
            cancel:'Sluit'
        } )).value;  

    async function onAddPayment() {
        try {
            var result = await appDlg.promised.create('salesinvoice_payment', {id:model.value.id});
            model.value.inv_open  = result.inv_open;
            model.value.inv_status  = result.inv_status;

        } catch (e) {} // canceled.
    }

    async function onOpenPayment(item) {
        try {
            var result = await appDlg.promised.open('salesinvoice_payment', {id:item.id});
            model.value.inv_open  = result.inv_open;
            model.value.inv_status  = result.inv_status;

        } catch (e) {} // canceled.
    }

    /**
     * Remove a paymment
     */
    async function removePayment(row) {
        var id_payment = row.id;
        var id_invoice = model.value.id;
        try {
            await noty.confirm("Weet u zeker dat u deze betaling wilt verwijderen?");
            model.value.isLoading = true;
            var result = await api.remove(id_invoice, id_payment);
            model.value.inv_total = result.data.inv_total;
            model.value.inv_open  = result.data.inv_open;
            model.value.inv_status  = result.data.inv_status;
            model.value.inv_number  = result.data.inv_number;
            dt.search();
            // Send a notification so that an underlying datatable triggers refreshment. 
            var sendData = {...result.data};
            sendData.id = sendData.id_invoice;
            eventbus.model.saved("salesinvoice", sendData);

        } finally {
            model.value.isLoading = false;
        }
    }
    window.dtma = model;
    window.dta = dt;
</script>